import { useState } from 'react';
import type { DiscoveryActivityEntry } from './activityLog.ts';

/**
 * The runner's activity is rebuilt from the latest poll every time, so a status line such as
 * "7 processed, 1 failed" is overwritten in place and stamped with the newest progress time,
 * which pins it to the bottom. This keeps what has been seen instead: when a line's text
 * changes it is added as a new line at its own time and the earlier one stays, so the log
 * reads as a history that flows in order.
 */
export interface ActivityHistory {
  /** What the history belongs to (the workspace); a new key starts it afresh. */
  key: string;
  /** Every line seen, in the order first seen. */
  lines: DiscoveryActivityEntry[];
  /** The latest version per source id, to tell a changed line from a repeat. */
  latest: ReadonlyMap<string, { text: string; versions: number; index: number }>;
}

export const emptyHistory = (key: string): ActivityHistory => ({
  key,
  lines: [],
  latest: new Map(),
});

/** How a line reads, apart from when it happened. */
const shape = (entry: DiscoveryActivityEntry) =>
  [entry.glyph, entry.actor, entry.tone, entry.ref].join('\u0000');

/**
 * Adds lines that are new or whose text changed. Transient status snapshots can opt into
 * in-place updates; other changed lines stay in the history. Returns `history` when unchanged.
 */
export function mergeActivity(
  history: ActivityHistory,
  incoming: DiscoveryActivityEntry[],
): ActivityHistory {
  let lines: DiscoveryActivityEntry[] | undefined;
  let latest: Map<string, { text: string; versions: number; index: number }> | undefined;

  for (const entry of incoming) {
    const known = (latest ?? history.latest).get(entry.id);
    const current = known && (lines ?? history.lines)[known.index];

    if (
      known &&
      current &&
      (known.text === entry.text || current.replaceInPlace || entry.replaceInPlace)
    ) {
      // A line first seen before the backend had a time takes the time once one arrives;
      // undated lines sort last, so keeping "—" would pin it to the bottom for good.
      const dated = Boolean(current.at) || !entry.at;
      if (known.text === entry.text && shape(current) === shape(entry) && dated) continue;
      lines ??= [...history.lines];
      lines[known.index] = {
        ...entry,
        id: current.id,
        at: current.at ?? entry.at,
        time: current.at ? current.time : entry.time,
      };
      if (known.text !== entry.text) {
        latest ??= new Map(history.latest);
        latest.set(entry.id, { text: entry.text, versions: known.versions, index: known.index });
      }
      continue;
    }

    lines ??= [...history.lines];
    latest ??= new Map(history.latest);
    // A superseded "Thinking" line is finished: it must stop animating once a newer line follows.
    if (known && current?.pending)
      lines[known.index] = {
        ...current,
        pending: false,
        isReasoning: false,
        reasoningLabel: undefined,
      };
    const versions = (known?.versions ?? 0) + 1;
    // The first version keeps its id; later ones get a suffix so every line has its own key.
    lines.push(versions === 1 ? entry : { ...entry, id: `${entry.id}~${versions}` });
    latest.set(entry.id, { text: entry.text, versions, index: lines.length - 1 });
  }

  return lines ? { key: history.key, lines, latest: latest ?? history.latest } : history;
}

export function useActivityHistory(
  incoming: DiscoveryActivityEntry[],
  key: string,
): DiscoveryActivityEntry[] {
  const [stored, setStored] = useState(() => emptyHistory(key));
  const current = stored.key === key ? stored : emptyHistory(key);
  const next = mergeActivity(current, incoming);
  // Derived state: set while rendering so the lines are right on this pass, not one later.
  if (next !== stored) setStored(next);
  return next.lines;
}
